import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2, LogOut, RefreshCw, ImageOff, Download, ScanLine,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SubmissionRow from "@/components/SubmissionRow";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "flagged", label: "Flagged" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const STAT_TILES = [
  { k: "total", label: "Submissions" },
  { k: "pending", label: "Pending" },
  { k: "flagged", label: "Flagged" },
  { k: "approved", label: "Approved" },
  { k: "tokens_issued", label: "Tokens Issued" },
  { k: "tokens_redeemed", label: "Redeemed" },
];

function ListEmpty() {
  return (
    <div className="border-2 border-dashed border-[#09090B] p-12 text-center">
      <ImageOff strokeWidth={2.5} className="w-10 h-10 mx-auto text-[#52525B]" />
      <h3 className="font-display font-bold text-xl mt-4">No submissions in this view.</h3>
      <p className="text-[#52525B] mt-2 text-sm">Try another tab.</p>
    </div>
  );
}

function ListLoading() {
  return <div className="ws-label text-center py-12">Loading…</div>;
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/submissions?status=${tab}`),
      ]);
      setStats(s.data);
      setItems(list.data || []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/submissions/${id}/approve`);
      await load();
    } finally { setBusy(null); }
  };
  const handleReject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/submissions/${id}/reject`);
      await load();
    } finally { setBusy(null); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/submissions/${id}`);
      await load();
    } finally { setBusy(null); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectableIds = useMemo(
    () => items.filter((i) => i.status !== "approved").map((i) => i.id),
    [items]
  );

  const toggleSelectAll = () => {
    if (selected.size === selectableIds.length) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    setBusy("bulk");
    try {
      await api.post(`/admin/submissions/bulk-approve`, { ids: Array.from(selected) });
      await load();
    } finally { setBusy(null); }
  };

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const { data } = await api.get("/admin/submissions.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `worksafestory-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setBusy(null); }
  };

  const allSelected = selectableIds.length > 0 && selected.size === selectableIds.length;

  return (
    <div className="bg-white min-h-screen text-[#09090B]">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B] bg-[#09090B] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="ws-label text-[#FFCC00]">/ Safety Admin</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">Dashboard</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/admin/redeem"
              data-testid="open-redeem-link"
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#09090B] ws-label px-4 py-2 border-2 border-[#FFCC00] hover:bg-white transition-colors"
            >
              <ScanLine strokeWidth={2.5} className="w-4 h-4" /> Redeem Tokens
            </Link>
            <Link
              to="/admin/leaderboard"
              data-testid="open-leaderboard-link"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white ws-label px-4 py-2 border-2 border-[#FF5A00] hover:bg-white hover:text-[#09090B] transition-colors"
            >
              <ScanLine strokeWidth={2.5} className="w-4 h-4" /> Leaderboard
            </Link>
            <Link
              to="/admin/store-qr"
              data-testid="open-store-qr-link"
              className="inline-flex items-center gap-2 bg-transparent text-white ws-label px-4 py-2 border-2 border-white hover:bg-white hover:text-[#09090B] transition-colors"
            >
              <ScanLine strokeWidth={2.5} className="w-4 h-4" /> Store Poster
            </Link>
            <Link
              to="/admin/wall-qr"
              data-testid="open-wall-qr-link"
              className="inline-flex items-center gap-2 bg-transparent text-white ws-label px-4 py-2 border-2 border-white hover:bg-white hover:text-[#09090B] transition-colors"
            >
              <ScanLine strokeWidth={2.5} className="w-4 h-4" /> Wall Poster
            </Link>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="export-csv-btn"
              disabled={busy === "csv"}
              className="inline-flex items-center gap-2 bg-white text-[#09090B] ws-label px-4 py-2 border-2 border-white hover:bg-[#F4F4F5] transition-colors disabled:opacity-60"
            >
              <Download strokeWidth={2.5} className="w-4 h-4" /> {busy === "csv" ? "…" : "Export CSV"}
            </button>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 bg-transparent text-white ws-label px-4 py-2 border-2 border-white hover:bg-[#FFCC00] hover:text-[#09090B] hover:border-[#FFCC00] transition-colors"
            >
              <RefreshCw strokeWidth={2.5} className="w-4 h-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/admin/login");
                logout();
              }}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white ws-label px-4 py-2 hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"
            >
              <LogOut strokeWidth={2.5} className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-6 gap-4">
        {STAT_TILES.map((s) => (
          <div key={s.k} className="border-2 border-[#09090B] bg-white p-5 ws-hard-shadow-sm" data-testid={`stat-${s.k}`}>
            <div className="ws-label text-[#52525B]">{s.label}</div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2">
              {stats ? stats[s.k] ?? 0 : "—"}
            </div>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex border-b-2 border-[#09090B] flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`ws-label px-6 py-3 -mb-[2px] border-b-4 transition-colors ${
                tab === t.key
                  ? "border-[#FF5A00] text-[#09090B]"
                  : "border-transparent text-[#52525B] hover:text-[#09090B]"
              }`}
            >
              {t.label} {stats && t.key !== "all" ? <span className="text-[#52525B]">({stats[t.key] ?? 0})</span> : null}
            </button>
          ))}
        </div>
      </section>

      {/* Bulk action bar */}
      {selectableIds.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
          <div className="flex items-center justify-between gap-4 border-2 border-dashed border-[#09090B] px-4 py-3 bg-[#F4F4F5]">
            <label className="ws-label inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                data-testid="select-all-checkbox"
                className="w-4 h-4 accent-[#FF5A00]"
              />
              {selected.size} selected
            </label>
            <button
              type="button"
              onClick={bulkApprove}
              disabled={selected.size === 0 || busy === "bulk"}
              data-testid="bulk-approve-btn"
              className="inline-flex items-center gap-2 bg-[#16A34A] text-white ws-label px-4 py-2 border-2 border-[#09090B] ws-hard-shadow-sm disabled:opacity-50"
            >
              <CheckCircle2 strokeWidth={2.5} className="w-4 h-4" />
              {busy === "bulk" ? "Approving…" : "Bulk approve"}
            </button>
          </div>
        </section>
      )}

      {/* List */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {loading && <ListLoading />}
        {!loading && items.length === 0 && <ListEmpty />}
        {!loading && items.length > 0 && (
          <div className="space-y-4">
            {items.map((it) => (
              <SubmissionRow
                key={it.id}
                submission={it}
                selected={selected}
                busy={busy}
                onToggleSelect={toggleSelect}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
